import React, { useRef, useState } from 'react'
import {
  type ActionOption,
  AddIcon as CreatePoolIcon,
  CloseIcon as ClosePoolIcon,
  ReportProblemIcon as ReportPoolIcon,
  IconButton,
  ActionMenu,
  OverflowIcon,
} from '@paypalcorp/pp-react'
import { sideBySideActionButton as sideBySideActionButtonStyle } from './index.css'
import * as styles from './moreOptionsButton.css'
import { useLocalizedContent } from '../../../../../hooks/useLocalizedContent'

export interface MoreOptionsItem {
  actionMenuItem: ActionOption
  onClick?: () => void
}

export const MORE_OPTIONS_ITEMS: Record<string, MoreOptionsItem> = {
  CREATE: {
    actionMenuItem: {
      label: 'flatProgress.createPool',
      decoration: (
        <CreatePoolIcon
          color="sysColorlinkMain"
          size="sm"
          data-testid="createPoolButton"
        />
      ),
    },
  },
  CLOSE: {
    actionMenuItem: {
      label: 'flatProgress.closePool',
      decoration: (
        <ClosePoolIcon
          color="sysColorlinkMain"
          size="sm"
          data-testid="closePoolButton"
        />
      ),
    },
  },
  REPORT: {
    actionMenuItem: {
      label: 'detail.reportGroup',
      decoration: (
        <ReportPoolIcon
          color="sysColorlinkMain"
          size="sm"
          data-testid="reportPoolButton"
        />
      ),
      navigationOptions: {
        href: '#',
        target: '_blank',
      },
    },
  },
}

interface MoreOptionsButtonProps {
  items: MoreOptionsItem[]
  iconSize?: 'sm' | 'xl' | 'lg'
  displayLabel: boolean
  onClickMoreOptions: () => void
}

const MoreOptionsButton = ({
  items,
  iconSize,
  displayLabel = false,
  onClickMoreOptions,
}: MoreOptionsButtonProps) => {
  const { getContent } = useLocalizedContent()
  const triggerRef = useRef()
  const [isOpen, setIsOpen] = useState(false)
  const actionItems = items.map((item) => item.actionMenuItem)

  const handleMoreOptionsItemClick = (index: number) => {
    setIsOpen(false)

    const clickedOption = items[index]

    if (clickedOption.onClick) {
      clickedOption.onClick()
    }
  }

  const onClick = () => {
    onClickMoreOptions()
    setIsOpen(true)
  }

  return (
    <div>
      <IconButton
        className={sideBySideActionButtonStyle}
        buttonType="secondary"
        onClick={onClick}
        svgIconComponent={OverflowIcon}
        ref={triggerRef}
        data-testid="moreOptionsButton"
        size={iconSize}
        label={displayLabel ? getContent('navBar.more') : undefined}
      />
      <ActionMenu
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        initialItemFocus={false}
        onSelection={handleMoreOptionsItemClick}
        triggerRef={triggerRef}
        mobileTitle={getContent('detail.moreOptions')}
        options={actionItems}
        itemClassName={styles.actionMenuItem}
      />
    </div>
  )
}

export default MoreOptionsButton
